import React, { useState } from 'react';
import type { PluginReadiness } from '../workspace/workspace-readiness.js';

export interface PluginDegradedBannerProps {
  readinessMap: Map<string, PluginReadiness>;
  onOpenSettings?: (pluginId?: string) => void;
}

export const PluginDegradedBanner: React.FC<PluginDegradedBannerProps> = ({
  readinessMap,
  onOpenSettings,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const degradedPlugins = Array.from(readinessMap.values()).filter(
    (r) => r.status === 'degraded' || r.status === 'blocked',
  );

  if (degradedPlugins.length === 0 || isDismissed) {
    return null;
  }

  const blockedCount = degradedPlugins.filter((r) => r.status === 'blocked').length;
  const degradedCount = degradedPlugins.filter((r) => r.status === 'degraded').length;

  return (
    <div
      role="alert"
      aria-label="Workspace degradation banner"
      style={{
        background: 'linear-gradient(90deg, rgba(234, 179, 8, 0.15) 0%, rgba(202, 138, 4, 0.1) 100%)',
        borderBottom: '1px solid rgba(234, 179, 8, 0.3)',
        padding: '8px 16px',
        color: '#fef08a',
        fontSize: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        zIndex: 50,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px' }}>⚠️</span>
          <span>
            <strong>Workspace Capabilities Degraded:</strong>{' '}
            {blockedCount > 0 && `${blockedCount} plugin(s) blocked. `}
            {degradedCount > 0 && `${degradedCount} plugin(s) running with limited features.`}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fde047',
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            {isExpanded ? 'Hide Details' : 'View Details'}
          </button>
          <button
            type="button"
            onClick={() => setIsDismissed(true)}
            aria-label="Dismiss banner"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '0 4px',
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {isExpanded && (
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.25)',
            borderRadius: '6px',
            padding: '8px 12px',
            marginTop: '4px',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          {degradedPlugins.map((r) => (
            <div key={r.pluginId} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
              <div>
                <span style={{ fontWeight: 600, color: r.status === 'blocked' ? '#f87171' : '#facc15' }}>
                  {r.pluginId} [{r.status.toUpperCase()}]:
                </span>{' '}
                <span style={{ color: '#cbd5e1' }}>{r.degradedMessages.join(' • ')}</span>
              </div>
              {onOpenSettings && (
                <button
                  type="button"
                  onClick={() => onOpenSettings(r.pluginId)}
                  style={{
                    background: 'rgba(234, 179, 8, 0.2)',
                    border: '1px solid rgba(234, 179, 8, 0.4)',
                    color: '#fef08a',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Configure
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
